import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BrowserMultiFormatReader } from "@zxing/browser";

import { NotFoundException } from "@zxing/library";

import { IconBulb, IconBulbOff } from "@tabler/icons-react";

import { space, fontSize } from "../../design/tokens";
import { Alert, CircularProgress, Box, Button, Text } from "../layout";

import { selectFoodByBarcode } from "../../store/selectors";



// ─── Debug overlay ────────────────────────────────────────────────────────────
// const log = (msg) => {
//   console.log(msg);
//   let d = document.getElementById("dbg");
//   if (!d) {
//     d = document.createElement("div");
//     d.id = "dbg";
//     d.style = "position:fixed;bottom:0;left:0;right:0;background:rgba(0,0,0,0.85);color:lime;font-size:11px;padding:8px;z-index:99999;max-height:40vh;overflow:auto;word-break:break-all;";
//     document.body.appendChild(d);
//   }
//   d.innerHTML += `<div>${new Date().toISOString().slice(11,19)} — ${msg}</div>`;
//   d.scrollTop = d.scrollHeight;
// };

const log = () => {}

// ─── Fetch product from Open Food Facts by barcode ────────────────────────────

async function fetchOFFByBarcode(barcode) {
  const url = `https://world.openfoodfacts.org/api/v2/product/${barcode}.json`;
  const res = await fetch(url, {
    headers: { "User-Agent": "SlimApp/1.0 (nutrition-tracker)" },
  });
  if (!res.ok) throw new Error(`off_${res.status}`);
  const data = await res.json();
  if (data.status !== 1) throw new Error("not_found");
  return data.product;
}

// ─── Map OFF product → food schema ───────────────────────────────────────────

export function mapOFFProductToFood(product) {
  const n = product.nutriments || {};

  const calories = Math.round(
    n["energy-kcal_100g"] ??
    (n["energy_100g"] ? n["energy_100g"] / 4.184 : 0)
  ) || 0;

  const protein = Math.round((n["proteins_100g"]      ?? 0) * 10) / 10;
  const carbs   = Math.round((n["carbohydrates_100g"] ?? 0) * 10) / 10;
  const fat     = Math.round((n["fat_100g"]           ?? 0) * 10) / 10;
  const fiber   = Math.round((n["fiber_100g"]         ?? 0) * 10) / 10;
  const sugar   = Math.round((n["sugars_100g"]        ?? 0) * 10) / 10;

  const name =
    product.product_name_en ||
    product.product_name    ||
    product.generic_name    ||
    "Unknown product";

  const tags = (product.categories_tags ?? []).join(" ").toLowerCase();
  let categorie = "other";
  if      (tags.includes("meat")      || tags.includes("poultry"))                         categorie = "meat";
  else if (tags.includes("fish")      || tags.includes("seafood"))                         categorie = "fish";
  else if (tags.includes("dairy")     || tags.includes("cheese") || tags.includes("milk")) categorie = "dairy";
  else if (tags.includes("vegetable"))                                                      categorie = "vegetable";
  else if (tags.includes("fruit"))                                                          categorie = "fruit";
  else if (tags.includes("grain")     || tags.includes("bread")  || tags.includes("cereal")) categorie = "grain";
  else if (tags.includes("beverage")  || tags.includes("drink"))                           categorie = "drink";
  else if (tags.includes("sweet")     || tags.includes("chocolate") || tags.includes("candy")) categorie = "sweet";
  else if (tags.includes("vegan")     || tags.includes("plant-based"))                     categorie = "vegan";

  return {
    id:          crypto.randomUUID(),
    name:        name.trim(),
    calories,
    protein,
    carbs,
    fat,
    fiber,
    sugar,
    categorie,
    servingSize: 100,
    unit:        "g",
    barcode:     product.code ?? null,
    source:      "openfoodfacts",
    imageUrl:    product.image_thumb_url || product.image_url || null,
  };
}

// ─── BarcodeScanner ───────────────────────────────────────────────────────────

export default function BarcodeScanner({ onResult, onDuplicate, onClose }) {
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const readerRef  = useRef(null);
  const mountedRef = useRef(true);

  const [status,        setStatus]        = useState("starting");
  const [errorMsg,      setErrorMsg]      = useState(null);
  const [torch,         setTorch]         = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState(null);

  const existingFood = useSelector(selectFoodByBarcode(scannedBarcode));

  // ── Stop camera stream ───────────────────────────────────────────────────────
  function stopScanner() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  // ── Start scanner ────────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    log("BarcodeScanner mounted");

    async function startScanner() {
      try {
        log("checking mediaDevices...");

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("getUserMedia not supported on this browser/context");
        }

        log("creating BrowserMultiFormatReader...");
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;

        log("requesting camera (environment)...");
        // const stream = await navigator.mediaDevices.getUserMedia({
        //   video: { facingMode: { ideal: "environment" } },
        // });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        })

        log("camera stream obtained ✓");

        if (!mountedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          log("component unmounted before stream attached — aborting");
          return;
        }

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        log("video playing ✓ — status → scanning");
        setStatus("scanning");

        reader.decodeFromStream(stream, videoRef.current, async (result, err) => {


            log("decode cb — result:" + !!result + " err:" + (err?.constructor?.name ?? "none"));

          if (!mountedRef.current) return;
          if (err instanceof NotFoundException) return;
          if (err) {
            log("decode err: " + err?.message);
            return;
          }

          if (result) {
            const barcode = result.getText();
            log("barcode detected: " + barcode);
            stopScanner();
            setScannedBarcode(barcode);
            setStatus("loading");

            try {
              log("fetching OFF for: " + barcode);
              const product = await fetchOFFByBarcode(barcode);
              log("OFF fetch success: " + product.product_name);
              const food = mapOFFProductToFood(product);
              onResult(food);
            } catch (e) {
              if (!mountedRef.current) return;
              log("OFF fetch error: " + e.message);
              setErrorMsg(
                e.message === "not_found"
                  ? `Barcode ${barcode} not found in Open Food Facts.`
                  : "Could not fetch product. Check your connection."
              );
              setStatus("error");
            }
          }
        });
      } catch (e) {
        if (!mountedRef.current) return;
        log("startScanner error: " + e.name + " — " + e.message);
        setErrorMsg(
          e.name === "NotAllowedError"
            ? "Camera access denied. Please allow camera in your browser settings."
            : "Could not start camera: " + e.message
        );
        setStatus("error");
      }
    }

    startScanner();

    return () => {
      mountedRef.current = false;
      stopScanner();
      log("BarcodeScanner unmounted");
    };
  }, []);

  // ── Duplicate check ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (existingFood && status === "loading") {
      log("duplicate found: " + existingFood.name);
      setStatus("duplicate");
      onDuplicate?.(existingFood);
    }
  }, [existingFood, status]);

  // ── Torch toggle ─────────────────────────────────────────────────────────────
  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch }] });
      setTorch((v) => !v);
    } catch {
      log("torch not supported");
    }
  }

  function retry() {
    setErrorMsg(null);
    setScannedBarcode(null);
    setStatus("starting");
    onClose();
  }

  return (
    <Box
      style={{
        position:       "relative",
        width:          "100%",
        maxWidth:       400,
        margin:         "auto",
        borderRadius:   2,
        overflow:       "hidden",
        background:     "black",
        aspectRatio:    "3/4",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
      }}
    >
      {/* Video feed */}
      <video
        ref={videoRef}
        style={{
          width:     "100%",
          height:    "100%",
          objectFit: "cover",
          display:   status === "scanning" ? "block" : "none",
        }}
        muted
        playsInline
      />

      {/* Scanning overlay */}
      {status === "scanning" && (
        <Box style={{
          position:       "absolute",
          inset:          0,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          pointerEvents:  "none",
          width:          "100%",
          height:         "100%",
        }}>
          <Box style={{
            position:        "absolute",
            inset:           0,
            background:      "rgba(0,0,0,0.45)",
            maskImage:       "radial-gradient(ellipse 65% 22% at 50% 50%, transparent 100%, black 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 65% 22% at 50% 50%, transparent 100%, black 100%)",
          }} />
          <Box style={{
            width:        "65%",
            height:       "22%",
            border:       "2px solid white",
            borderRadius: 1,
          }} />
          <Text style={{
            position: "absolute",
            bottom:   "28%",
            color:    "rgba(255,255,255,0.8)",
            fontSize: "12px",
          }}>
            Aim at barcode
          </Text>
        </Box>
      )}

      {status === "starting" && <CircularProgress color="inherit" />}

      {status === "loading" && (
        <Box style={{ textAlign: "center", color: "white" }}>
          <CircularProgress color="inherit" style={{ marginBottom: space.xs }} />
          <Text style={{ fontSize: "14px" }}>Looking up product…</Text>
        </Box>
      )}

      {status === "duplicate" && (
        <Box style={{ padding: space.md, textAlign: "center" }}>
          <Alert severity="info" style={{ marginBottom: space.md, fontSize: "13px" }}>
            <strong>{existingFood?.name}</strong> is already in your food list.
          </Alert>
          <Button onClick={() => { stopScanner(); onClose(); }} style={{ borderColor: "white" }}>
            Go back
          </Button>
        </Box>
      )}

      {status === "error" && (
        <Box style={{ padding: space.md, textAlign: "center" }}>
          <Alert severity="warning" style={{ marginBottom: space.md, fontSize: "13px" }}>
            {errorMsg}
          </Alert>
          <Button onClick={retry} style={{ borderColor: "white" }}>
            Try again
          </Button>
        </Box>
      )}

      {/* Close button */}
      <Button
        onClick={() => { stopScanner(); onClose(); }}
        style={{
          position:       "absolute",
          display:        "flex",
          justifyContent: "center",
          alignItems:     "center",
          fontSize:       fontSize.lg,
          top:            "8px",
          right:          "8px",
          width:          "20px",
          height:         "20px",
          borderRadius:   "0px",
          color:          "white",
          background:     "none",
        }}
      >
        &times;
      </Button>

      {/* Torch button */}
      {status === "scanning" && (
        <Button
          onClick={toggleTorch}
          style={{
            position:       "absolute",
            display:        "flex",
            justifyContent: "center",
            alignItems:     "center",
            padding:        "0",
            bottom:         "8px",
            right:          "8px",
            width:          "30px",
            height:         "30px",
            borderRadius:   "0px",
            color:          "white",
            background:     "none",
          }}
        >
          {torch ? <IconBulbOff size={30} /> : <IconBulb size={30} />}
        </Button>
      )}
    </Box>
  );
}
